import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

const useNotificationsList = () => {
  const [notifications, setNotifications] = useState([]);
  const notifCount = notifications.filter(n => !n.read).length;
  const fetchRef = useRef(null);
  const { ixBiz } = useSelector(state => state.socketio);

  const fetchNotifications = useCallback(() => {
    const storedNotifications =
      JSON.parse(localStorage.getItem('CommandsReceived')) || {};
    const notificationsArray = Object.entries(storedNotifications)
      .filter(([, value]) => value.biz === ixBiz)
      .sort(([a], [b]) => b - a) // Sort newest first
      .map(([key, value]) => ({
        ...value,
        notifKey: key
      }));
    setNotifications(notificationsArray);
  }, []);

  fetchRef.current = fetchNotifications;

  const LISTENERS = useMemo(() => ({
    storage: event => {
      if (event.key === 'CommandsReceived') {
        fetchRef.current();
      }
    },
    CommandsUpdated: () => {
      fetchRef.current();
    }
  }));

  useEffect(() => {
    fetchNotifications(); // Initial load
    fetchRef.current = fetchNotifications;
    // Attach event listeners dynamically
    Object.entries(LISTENERS).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    // Cleanup: Remove listeners on unmount
    return () => {
      Object.entries(LISTENERS).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, [fetchNotifications]);

  // deletes
  const handleDeleteNotification = useCallback(notifKey => {
    const storedNotifications =
      JSON.parse(localStorage.getItem('CommandsReceived')) || {};

    delete storedNotifications[notifKey];

    localStorage.setItem(
      'CommandsReceived',
      JSON.stringify(storedNotifications)
    );

    setNotifications(prev => {
      const updatedNotifications = prev.filter(
        notif => notif.notifKey !== notifKey
      );
      return updatedNotifications;
    });
  }, []);

  const clearAllNotifications = useCallback(() => {
    localStorage.removeItem('CommandsReceived');
    setNotifications([]);
  }, []);

  // mark as read
  const markNotificationAsRead = useCallback(notifKey => {
    const storedNotifications =
      JSON.parse(localStorage.getItem('CommandsReceived')) || {};

    if (storedNotifications[notifKey]) {
      storedNotifications[notifKey].read = true;

      localStorage.setItem(
        'CommandsReceived',
        JSON.stringify(storedNotifications)
      );

      setNotifications(prev => {
        return prev.map(notif =>
          notif.notifKey === notifKey ? { ...notif, read: true } : notif
        );
      });

      window.dispatchEvent(new Event('CommandsUpdated'));
    }
  }, []);
  return {
    notifications,
    setNotifications,
    handleDeleteNotification,
    clearAllNotifications,
    markNotificationAsRead,
    notifCount
  };
};

export default useNotificationsList;
